import {
  ALL_PWS_MODULE_MENU_IDS,
  PWS_COMPLIANCE_MENU_IDS,
  RECLAIMED_MENU_IDS,
  userHasPwsModuleActive,
  userHasPwsRole,
  userHasCompanyAssignment
} from './pwsModuleAccess';
import { userHasTodoListModule } from './todoModuleAccess';
import { isBugReportAdmin } from './bugReportAccess';
import {
  DISPOSAL_SITES_PAGE_ID,
  INVOICING_PAGE_ID,
  WASTE_GENERATOR_SITES_PAGE_ID,
  pagesForModules
} from './permissions';

function filterNavigationMenu(menuItems, user) {
    if (!user) return [];
    // Get user roles from the roles array - handle different possible structures.
    // Normalize to lowercase so DB values like "FacilityUser" match keys and checks (e.g. 'facilityuser').
    const normalizeRoleKey = (roleName) =>
      String(roleName || '').toLowerCase().replace(/\s+/g, '');

    let userRoles = [];
    if (user.roles && Array.isArray(user.roles)) {
      userRoles = user.roles.map(role => {
        const name = role.roleName ?? role;
        return typeof name === 'string' ? normalizeRoleKey(name) : name;
      });
    } else if (user.roles && typeof user.roles === 'string') {
      userRoles = [user.roles.toLowerCase()];
    } else if (user.role) {
      userRoles = [typeof user.role === 'string' ? user.role.toLowerCase() : user.role];
    }

    // Also check for role in user object properties (fallback)
    if (userRoles.length === 0 && user.roleName) {
      userRoles = [typeof user.roleName === 'string' ? user.roleName.toLowerCase() : user.roleName];
    }

    // Get customer assignments, active modules, and RACI assignments (support multiple key shapes)
    const companyAssignments = user.companyAssignments ?? [];
    const activeModules = user.activeModules ?? [];
    const activeModuleCodes = user.activeModuleCodes ?? [];
    const raciAssignments = user.raciAssignments ?? [];

    const normalizeActiveModuleCode = (entry) => {
      const raw = (entry?.code ?? entry?.Code ?? entry ?? '')
        .toString()
        .toUpperCase()
        .trim()
        .replace(/\s+/g, '_');
      return raw;
    };
    const normalizeModuleName = (value) => {
      if (value == null) return '';
      return String(value).trim().toLowerCase().replace(/\s+/g, ' ');
    };
    const compactModuleToken = (value) =>
      normalizeModuleName(String(value || '').replace(/[_-]+/g, ' ')).replace(/\s+/g, '');
    const isEnvironmentalSamplingToken = (token) =>
      Boolean(token) && /environmental.*sampl/.test(token);
    const isEnvironmentalLabToken = (token) =>
      Boolean(token) && (/environmental.*lab/.test(token) || token.includes('labanalysis'));
    const moduleEntryToken = (entry) => compactModuleToken(
      entry?.ModuleName ??
      entry?.moduleName ??
      entry?.FormTitle ??
      entry?.FormName ??
      entry?.formName ??
      entry?.Title ??
      entry?.Name ??
      entry?.code ??
      entry?.Code ??
      entry
    );
    const hasEnvironmentalSamplingActive = () => {
      const modules = Array.isArray(activeModules) ? activeModules : [];
      if (modules.some((m) => {
        if (Number(m?.FormTypeID ?? m?.formTypeID) === 13) return true;
        const token = moduleEntryToken(m);
        return isEnvironmentalSamplingToken(token) || isEnvironmentalLabToken(token);
      })) {
        return true;
      }
      return (Array.isArray(activeModuleCodes) ? activeModuleCodes : []).some((entry) => {
        const token = compactModuleToken(normalizeActiveModuleCode(entry));
        return isEnvironmentalSamplingToken(token) || isEnvironmentalLabToken(token);
      });
    };
    const hasVerificationsModuleCode = (entry) => {
      const code = normalizeActiveModuleCode(entry);
      return code.includes('VERIF') || code.includes('AUDIT');
    };
    const hasConstructionModuleCode = (entry) => {
      const code = normalizeActiveModuleCode(entry);
      if (!code) return false;
      return (
        code === 'CONSTRUCTION' ||
        code === 'CP' ||
        code === 'PROJECTS' ||
        code === 'CONSTRUCTION_PERMITTING' ||
        code.includes('CONSTRUCTION')
      );
    };
    const hasConstructionModuleByName = (modules) =>
      Array.isArray(modules) &&
      modules.some((m) => {
        const moduleName = normalizeModuleName(
          m?.ModuleName ??
            m?.moduleName ??
            m?.FormTitle ??
            m?.FormName ??
            m?.Title ??
            m?.Name ??
            m
        );
        return (
          moduleName.includes('construction') ||
          moduleName === 'project permitting' ||
          moduleName.includes('project permitting')
        );
      });
    const hasConstructionRole = userRoles.some((role) =>
      ['companyadmin', 'constructionmanager', 'constructionuser', 'globaladmin'].includes(role)
    );
    const hasComplianceOrdersRole = userRoles.some(role =>
      ['complianceordermanager', 'complianceorderuser', 'companyadmin', 'globaladmin'].includes(role)
    );
    const hasConocoTasksRole = userRoles.some(role =>
      ['conocotaskmanager', 'conocotaskuser', 'companyadmin', 'globaladmin'].includes(role)
    );
    const hasComplianceOrdersInActiveModules = () => {
      const hasByCode = Array.isArray(activeModuleCodes) &&
        activeModuleCodes.some((m) => {
          const code = normalizeActiveModuleCode(m);
          return code === 'COMPLIANCE_ORDERS' || code === 'COMPLIANCEORDERS';
        });
      if (hasByCode) return true;

      const complianceOrderNamePatterns = ['compliance orders', 'compliance order'];
      const hasByName = Array.isArray(activeModules) && activeModules.some((m) => {
        const moduleName = normalizeModuleName(
          m?.ModuleName ??
          m?.moduleName ??
          m?.FormTitle ??
          m?.FormName ??
          m?.Title ??
          m?.Name ??
          m
        );
        return complianceOrderNamePatterns.some(pattern =>
          moduleName === pattern || moduleName.includes(pattern)
        );
      });
      return hasByName;
    };
    const hasConocoTasksInActiveModules = () => {
      const hasByCode = Array.isArray(activeModuleCodes) &&
        activeModuleCodes.some((m) => {
          const code = normalizeActiveModuleCode(m);
          return code === 'CONOCO_TASKS' || code === 'CONOCOTASKS';
        });
      if (hasByCode) return true;
      const hasByName = Array.isArray(activeModules) && activeModules.some((m) => {
        const moduleName = normalizeModuleName(
          m?.ModuleName ?? m?.moduleName ?? m?.FormTitle ?? m?.FormName ?? m?.Title ?? m?.Name ?? m
        );
        return moduleName.includes('conoco task') || moduleName === 'conoco tasks';
      });
      return hasByName;
    };

    // Build a set of allowed item IDs based on all user roles and active modules
    let allowedItems = new Set();

    // Define role-specific item mappings
    const roleItemMappings = {
      // Global admin sees everything; audits are still gated by the VERIFICATIONS module below
      globaladmin: [
        'home', 'todo-list', 'calendar', 'dashboard', 'compliance-orders-report', 'compliance-map', 'compliance-summary-report', 'audits-report', 'pws-report',
        'compliance-records', 'audits', 'audit-scopes', 'pre-audit-questionnaires', 'standards-and-regulations',
        'conoco-tasks', 'conoco-task-groups',
        'compliance-orders', 'compliance-order-management', 'compliance-order-verifications', 'compliance-order-action-codes', 'compliance-order-work-types',
        'add-engine', 'add-engine-use', 'engine-running-hours', 'fields', 'wells',
        'air-glycol-pumps', 'air-flares', 'air-glycol-pump-log', 'air-opacity-monitoring', 'air-flare-log',
        'drainage-recordation', 'spcc-routine-inspection', 'spcc-annual-inspection', 
        'swppp-discharge-monitoring-inspection', 'swppp-quarterly-inspection',
        'qi-notification-exercise-log', 'annual-tabletop-exercise-log', 'equipment-deployment-exercise-log',
        'transfer-hose-and-pipe-testing', 'environmental-sampling', 'environmental-lab-analysis',
        'hazardous-waste-removal', 'sump-pump-maintenance-inspection',
        'response-equipment-inventory-inspection',
        'project-permitting',
        'project-files',
        'weekly-flow-meter-logs', 'weekly-equipment-service-logs', 'weekly-pws-water-monitoring',
        'monthly-pws-water-sample', 'monthly-pws-analytical-report-review', 'quarterly-dlqor',
        'quarterly-colorimeter-calibration-verification', 'weekly-reclaimed-water-monitoring',
        'monthly-reclaimed-water-sample', 'monthly-reclaimed-analytical-report-review', 'monthly-lab-summary',
        'semi-annual-reclaimed-water-sample', 'semi-annual-reclaimed-analytical-report-review',
        'annual-reclaimed-water-sample', 'annual-reclaimed-analytical-report-review',
        'annual-analytical-data-summary',
        'authorized-waste-receipt', 'analytical-result-form-for-awr', 'outgoing-solid-waste',
        'outgoing-produced-water-waste', 'awr-approvers', 'r9-quarterly-report', INVOICING_PAGE_ID, 
        WASTE_GENERATOR_SITES_PAGE_ID, 'billing-companies', 'waste-transporters', DISPOSAL_SITES_PAGE_ID,
        'scheduler-monitor', 'qa-lab', 'custom-form-builder', 'custom-form-entry', 'email-test', 'pdf-testing', 'site-management', 'pws-log-record-assignment', 'form-status-management', 'templates', 'company-management', 'governing-bodies', 'manage-compliance-elements', 
        'manage-compliance-aspects', 'manage-record-types', 'manage-project-types', 'manage-permit-agencies',
        'manage-project-permit-types',
        'manage-project-elements',
        'manage-approval-parameters', 'hazardous-waste-disposal-site-management',
        'waste-units-and-conversions', 'sharepoint-documents', 'sharepoint-admin-resources',
        'library-of-records', 'users', 'user-access-matrix', 'settings'
      ],
      
      // R9 admin sees R9 pages + Invoicing (operational invoice review in Accounting Review).
      r9admin: [
        'home', 'todo-list', 'calendar', 'dashboard', 'compliance-summary-report',
        'authorized-waste-receipt', 'analytical-result-form-for-awr', 'outgoing-solid-waste',
        'outgoing-produced-water-waste', 'awr-approvers', 'r9-quarterly-report', INVOICING_PAGE_ID,
        WASTE_GENERATOR_SITES_PAGE_ID, 'billing-companies', 'waste-transporters', DISPOSAL_SITES_PAGE_ID,
        'settings'
      ],
      
      // Company admin sees pages for modules that are active on an assigned company (except administration).
      companyadmin: [
        'home', 'todo-list', 'calendar', 'dashboard', 'compliance-orders-report', 'compliance-map', 'compliance-summary-report', 'audits-report',
        'compliance-records', 'conoco-tasks', 'conoco-task-groups', 'audits', 'audit-scopes', 'pre-audit-questionnaires', 'standards-and-regulations',
        'compliance-orders', 'compliance-order-management', 'compliance-order-verifications',
        'add-engine', 'add-engine-use', 'engine-running-hours', 'fields', 'wells',
        'air-glycol-pumps', 'air-flares', 'air-glycol-pump-log', 'air-opacity-monitoring', 'air-flare-log',
        'drainage-recordation', 'spcc-routine-inspection', 'spcc-annual-inspection', 
        'swppp-discharge-monitoring-inspection', 'swppp-quarterly-inspection',
        'qi-notification-exercise-log', 'annual-tabletop-exercise-log', 'equipment-deployment-exercise-log',
        'transfer-hose-and-pipe-testing', 'environmental-sampling', 'environmental-lab-analysis',
        'hazardous-waste-removal', 'sump-pump-maintenance-inspection',
        'response-equipment-inventory-inspection',
        'project-permitting',
        'project-files',
        'manage-permit-agencies',
        'manage-project-permit-types',
        'manage-project-elements',
        'weekly-flow-meter-logs', 'weekly-equipment-service-logs', 'weekly-pws-water-monitoring',
        'monthly-pws-water-sample', 'monthly-pws-analytical-report-review', 'quarterly-dlqor',
        'quarterly-colorimeter-calibration-verification', 'weekly-reclaimed-water-monitoring',
        'monthly-reclaimed-water-sample', 'monthly-reclaimed-analytical-report-review', 'monthly-lab-summary',
        'semi-annual-reclaimed-water-sample', 'semi-annual-reclaimed-analytical-report-review',
        'annual-reclaimed-water-sample', 'annual-reclaimed-analytical-report-review',
        'authorized-waste-receipt', 'analytical-result-form-for-awr', 'outgoing-solid-waste',
        'outgoing-produced-water-waste', 'awr-approvers', 'r9-quarterly-report',
        'library-of-records', 'settings'
      ],
      
      // R9 manager sees R9 pages + basic access. Invoicing and AWR Approvers are r9admin / companyadmin / globaladmin only.
      r9manager: [
        'home', 'todo-list', 'calendar', 'dashboard',
        'authorized-waste-receipt', 'analytical-result-form-for-awr', 'outgoing-solid-waste',
        'outgoing-produced-water-waste', 'r9-quarterly-report',
        WASTE_GENERATOR_SITES_PAGE_ID, 'billing-companies', 'waste-transporters', DISPOSAL_SITES_PAGE_ID,
        'settings'
      ],
      
      // Compliance manager sees compliance records + ITM/testing pages + basic access
      compliancemanager: [
        'home', 'todo-list', 'calendar', 'dashboard', 'compliance-map',
        'compliance-records',
        'drainage-recordation', 'spcc-routine-inspection', 'spcc-annual-inspection', 
        'swppp-discharge-monitoring-inspection', 'swppp-quarterly-inspection',
        'qi-notification-exercise-log', 'annual-tabletop-exercise-log', 'equipment-deployment-exercise-log',
        'transfer-hose-and-pipe-testing', 'environmental-sampling', 'environmental-lab-analysis',
        'hazardous-waste-removal', 'sump-pump-maintenance-inspection',
        'response-equipment-inventory-inspection',
        'library-of-records', 'settings'
      ],
      
      // Facility manager sees facility pages + basic access
      facilitymanager: [
        'home', 'todo-list', 'calendar', 'dashboard',
        'add-engine', 'add-engine-use', 'engine-running-hours', 'fields', 'wells',
        'air-glycol-pump-log', 'air-opacity-monitoring', 'air-flare-log',
        'drainage-recordation', 'spcc-routine-inspection', 'spcc-annual-inspection', 
        'swppp-discharge-monitoring-inspection', 'swppp-quarterly-inspection',
        'qi-notification-exercise-log', 'annual-tabletop-exercise-log', 
        'equipment-deployment-exercise-log',
        'transfer-hose-and-pipe-testing', 'environmental-sampling', 'environmental-lab-analysis',
        'hazardous-waste-removal', 'sump-pump-maintenance-inspection',
        'response-equipment-inventory-inspection',
        'settings'
      ],
      
      // Construction manager sees construction pages + basic access
      constructionmanager: [
        'home', 'todo-list', 'calendar', 'dashboard',
        'project-permitting',
        'project-files',
        'qi-notification-exercise-log', 'annual-tabletop-exercise-log', 
        'transfer-hose-and-pipe-testing', 'environmental-sampling', 'environmental-lab-analysis',
        'hazardous-waste-removal', 'sump-pump-maintenance-inspection',
        'settings'
      ],
      
      // PWS manager sees PWS pages + basic access
      pwsmanager: [
        'home', 'todo-list', 'calendar', 'dashboard', 'pws-report',
        'weekly-flow-meter-logs', 'weekly-equipment-service-logs', 'weekly-pws-water-monitoring',
        'monthly-pws-water-sample', 'monthly-pws-analytical-report-review', 'quarterly-dlqor',
        'quarterly-colorimeter-calibration-verification', 'weekly-reclaimed-water-monitoring',
        'monthly-reclaimed-water-sample', 'monthly-reclaimed-analytical-report-review', 'monthly-lab-summary',
        'semi-annual-reclaimed-water-sample', 'semi-annual-reclaimed-analytical-report-review',
        'annual-reclaimed-water-sample', 'annual-reclaimed-analytical-report-review',
        'settings'
      ],
      
      // Task manager sees task pages + basic access
      taskmanager: [
        'home', 'todo-list', 'calendar', 'dashboard', 'compliance-summary-report',
        'settings'
      ],

      conocotaskmanager: [
        'home', 'todo-list', 'calendar', 'dashboard',
        'conoco-tasks', 'conoco-task-groups',
        'settings'
      ],
      conocotaskuser: [
        'home', 'todo-list', 'calendar', 'dashboard',
        'conoco-tasks',
        'settings'
      ],
      
      // Compliance order manager/user â€“ rely on module activation but still need explicit items
      complianceordermanager: [
        'home', 'todo-list', 'calendar', 'dashboard', 'compliance-orders-report',
        'compliance-orders', 'compliance-order-management', 'compliance-order-verifications',
        'fields', 'wells',
        'settings'
      ],
      complianceorderuser: [
        'home', 'todo-list', 'calendar', 'dashboard', 'compliance-orders-report',
        'compliance-orders', 'compliance-order-management', 'compliance-order-verifications',
        'fields', 'wells',
        'settings'
      ],
      
      // Verification manager sees audits + basic access; audits still require the VERIFICATIONS module to be active
      verificationmanager: [
        'home', 'todo-list', 'calendar', 'dashboard', 'audits-report',
        'audits', 'audit-scopes', 'pre-audit-questionnaires', 'standards-and-regulations',
        'settings'
      ],
      
      // Compliance user sees compliance pages + basic access (but limited by RACI)
      complianceuser: [
        'home', 'todo-list', 'calendar', 'dashboard',
        'compliance-records',
        'library-of-records', 'settings'
      ],
      
      // Facility user sees facility pages + basic access (but limited by RACI)
      facilityuser: [
        'home', 'todo-list', 'calendar', 'dashboard',
        'drainage-recordation', 'spcc-routine-inspection', 'spcc-annual-inspection', 
        'swppp-discharge-monitoring-inspection', 'swppp-quarterly-inspection',
        'qi-notification-exercise-log', 'annual-tabletop-exercise-log', 
        'equipment-deployment-exercise-log',
        'transfer-hose-and-pipe-testing', 'environmental-sampling', 'environmental-lab-analysis',
        'hazardous-waste-removal', 'sump-pump-maintenance-inspection',
        'response-equipment-inventory-inspection',
        'air-glycol-pump-log', 'air-opacity-monitoring', 'air-flare-log',
        'settings'
      ],
      
      // Construction user sees construction pages + basic access
      constructionuser: [
        'home', 'todo-list', 'calendar', 'dashboard',
        'project-permitting', 'project-files', 'settings'
      ],
      
      // PWS user sees PWS pages + basic access (but limited by RACI)
      pwsuser: [
        'home', 'todo-list', 'calendar', 'dashboard',
        'weekly-flow-meter-logs', 'weekly-equipment-service-logs', 'weekly-pws-water-monitoring',
        'monthly-pws-water-sample', 'monthly-pws-analytical-report-review', 'quarterly-dlqor',
        'quarterly-colorimeter-calibration-verification', 'weekly-reclaimed-water-monitoring',
        'monthly-reclaimed-water-sample', 'monthly-reclaimed-analytical-report-review', 'monthly-lab-summary',
        'semi-annual-reclaimed-water-sample', 'semi-annual-reclaimed-analytical-report-review',
        'annual-reclaimed-water-sample', 'annual-reclaimed-analytical-report-review',
        'settings'
      ],
      
      // R9 user sees R9 facility pages + basic access (NOT R9 administration)
      r9user: [
        'home', 'todo-list', 'calendar', 'dashboard',
        'authorized-waste-receipt', 'analytical-result-form-for-awr', 'outgoing-solid-waste',
        'outgoing-produced-water-waste',
        'settings'
      ],
      
      // Accounting manager sees Invoicing + basic (no other R9 admin)
      accountingmanager: [
        'home', 'todo-list', 'calendar', 'dashboard', INVOICING_PAGE_ID, 'settings'
      ],

      // Lower level roles see basic access only
      consultant: ['home', 'todo-list', 'calendar', 'dashboard', 'settings'],
      external: ['home', 'todo-list', 'calendar', 'dashboard', 'settings'],
      guest: ['home', 'todo-list', 'calendar', 'dashboard', 'settings'],
      user: ['home', 'todo-list', 'calendar', 'dashboard', 'settings']
    };

    // Process each role and add allowed items
    userRoles.forEach(role => {
      if (roleItemMappings[role]) {
        roleItemMappings[role].forEach(itemId => {
          allowedItems.add(itemId);
        });
      }
    });

    const COMPLIANCE_RECORD_ROLES = ['globaladmin', 'companyadmin', 'compliancemanager', 'complianceuser'];
    const hasComplianceRole = userRoles.some((role) => COMPLIANCE_RECORD_ROLES.includes(role));

    // Home is always available. Compliance Records is role-based, not gated on company modules.
    // To-Do List is added for all roles, then gated below by the company TODO_LIST module.
    allowedItems.add('home');
    allowedItems.add('todo-list');
    allowedItems.add('settings');
    if (hasComplianceRole) {
      allowedItems.add('compliance-records');
    }

    // Special handling for role precedence
    // If user has admin/manager roles, they take precedence over user roles for conflicting items
    const adminRoles = ['globaladmin', 'companyadmin'];
    const r9AdminRoles = ['r9admin'];
    const managerRoles = ['r9manager', 'compliancemanager', 'facilitymanager', 'constructionmanager', 'taskmanager', 'verificationmanager'];
    const createRoles = ['globaladmin', 'companyadmin', 'r9manager', 'compliancemanager', 'facilitymanager', 'constructionmanager', 'pwsmanager', 'taskmanager', 'verificationmanager'];
    
    // If user has global admin role, they get all admin items
    if (userRoles.some(role => role === 'globaladmin')) {
      ['users', 'user-access-matrix', 'company-management', 'site-management', 'pws-log-record-assignment', 'form-status-management'].forEach(itemId => {
        allowedItems.add(itemId);
      });
    }
    
    // R9 admin gets R9-specific admin items but not general admin items
    if (userRoles.some(role => r9AdminRoles.includes(role))) {
      ['awr-approvers', WASTE_GENERATOR_SITES_PAGE_ID, 'billing-companies', 'waste-transporters', DISPOSAL_SITES_PAGE_ID].forEach(itemId => {
        allowedItems.add(itemId);
      });
    }

    // Accounting manager gets Invoicing only (with basic pages)
    if (userRoles.includes('accountingmanager')) {
      allowedItems.add(INVOICING_PAGE_ID);
    }
    
    // If user has manager role, they get management items
    if (userRoles.some(role => managerRoles.includes(role))) {
      ['manage-compliance-elements', 'manage-compliance-aspects', 'manage-record-types', 'manage-project-types', 'manage-permit-agencies', 'manage-project-permit-types', 'manage-project-elements', 'manage-approval-parameters'].forEach(itemId => {
        allowedItems.add(itemId);
      });
    }

    // Check if user can create records based on their role
    const canCreateRecords = userRoles.some(role => createRoles.includes(role));
    
    // Handle special cases for roles that can see but not create
    if (userRoles.includes('complianceuser')) {
      // Compliance users can see compliance records but not create them
      // They can also see facility pages but not create records
      allowedItems.add('compliance-records');
      allowedItems.add('drainage-recordation');
      allowedItems.add('spcc-routine-inspection');
      allowedItems.add('spcc-annual-inspection');
      allowedItems.add('swppp-discharge-monitoring-inspection');
      allowedItems.add('swppp-quarterly-inspection');
      allowedItems.add('qi-notification-exercise-log');
      allowedItems.add('annual-tabletop-exercise-log');
      allowedItems.add('transfer-hose-and-pipe-testing');
      allowedItems.add('environmental-sampling');
      allowedItems.add('environmental-lab-analysis');
      allowedItems.add('hazardous-waste-removal');
      allowedItems.add('sump-pump-maintenance-inspection');
    }
    
    if (userRoles.includes('facilityuser')) {
      // Facility users can see facility pages but not create records
      allowedItems.add('drainage-recordation');
      allowedItems.add('spcc-routine-inspection');
      allowedItems.add('spcc-annual-inspection');
      allowedItems.add('swppp-discharge-monitoring-inspection');
      allowedItems.add('swppp-quarterly-inspection');
      allowedItems.add('qi-notification-exercise-log');
      allowedItems.add('annual-tabletop-exercise-log');
      allowedItems.add('transfer-hose-and-pipe-testing');
      allowedItems.add('environmental-sampling');
      allowedItems.add('environmental-lab-analysis');
      allowedItems.add('hazardous-waste-removal');
      allowedItems.add('sump-pump-maintenance-inspection');
    }
    
    if (userRoles.includes('pwsuser')) {
      // PWS users can see PWS pages but not create records
      allowedItems.add('weekly-flow-meter-logs');
      allowedItems.add('weekly-equipment-service-logs');
      allowedItems.add('weekly-pws-water-monitoring');
      allowedItems.add('monthly-pws-water-sample');
      allowedItems.add('monthly-pws-analytical-report-review');
      allowedItems.add('quarterly-dlqor');
      allowedItems.add('quarterly-colorimeter-calibration-verification');
      allowedItems.add('weekly-reclaimed-water-monitoring');
      allowedItems.add('monthly-reclaimed-water-sample');
      allowedItems.add('monthly-reclaimed-analytical-report-review');
      allowedItems.add('semi-annual-reclaimed-water-sample');
      allowedItems.add('semi-annual-reclaimed-analytical-report-review');
      allowedItems.add('annual-reclaimed-water-sample');
      allowedItems.add('annual-reclaimed-analytical-report-review');
    }
    
    if (userRoles.includes('r9user')) {
      // R9 users can see R9 facility pages but not create records
      allowedItems.add('authorized-waste-receipt');
      allowedItems.add('analytical-result-form-for-awr');
      allowedItems.add('outgoing-solid-waste');
      allowedItems.add('outgoing-produced-water-waste');
    }

    if (userRoles.includes('constructionuser')) {
      allowedItems.add('project-permitting');
      allowedItems.add('project-files');
    }

    // Company assignment is the first gate. Without it, non-global-admins get no company data or modules.
    if (!userRoles.includes('globaladmin') && companyAssignments.length === 0) {
      allowedItems = new Set(['home', 'settings']);
    }
    
    // Skip active module filtering only for global admin
    if (userRoles.some(role => ['globaladmin'].includes(role))) {
      // Skip filtering for global admin
    } else if (activeModules && activeModules.length > 0) {
      
      // Create a mapping between FormTypeID and menu item IDs
      const moduleToMenuItemMapping = {
        1: 'spcc-routine-inspection',      // SPCC Routine Inspection
        2: 'spcc-annual-inspection',       // SPCC Annual Inspection
        3: 'drainage-recordation',         // Drainage Recordation
        4: 'annual-tabletop-exercise-log', // Annual Tabletop Exercise Log
        5: 'transfer-hose-and-pipe-testing', // Transfer Hose and Pipe Testing
        6: 'qi-notification-exercise-log', // QI Notification Exercise Log
        7: 'response-equipment-inventory-inspection', // Response Equipment Inventory Inspection
        8: 'equipment-deployment-exercise-log', // Equipment Deployment Exercise Log
        9: 'sump-pump-maintenance-inspection', // Sump Pump Maintenance Inspection
        10: 'swppp-discharge-monitoring-inspection', // SWPPP Discharge Monitoring Inspection
        11: 'swppp-quarterly-inspection',  // SWPPP Quarterly Inspection
        13: 'environmental-sampling',      // Environmental Sampling
        15: 'project-permitting',          // Construction Permitting
        17: 'hazardous-waste-removal',     // Hazardous Waste Removal
        18: 'weekly-pws-water-monitoring', // PWS (this maps to multiple PWS pages)
        19: 'weekly-reclaimed-water-monitoring' // Reclaimed Water (this maps to multiple reclaimed water pages)
  };
      
      // Get the FormTypeIDs from active modules (support various shapes: { FormTypeID }, { formTypeID }, or plain number)
      const rawIds = activeModules.map(module => {
        if (module == null) return null;
        if (typeof module === 'number' && !Number.isNaN(module)) return module;
        const m = typeof module === 'object' ? module : {};
        return m.FormTypeID ?? m.formTypeID ?? m.formTypeId ?? null;
      }).filter(id => id != null && id !== '');
      const activeFormTypeIds = [...new Set(rawIds.map(id => Number(id)).filter(n => !Number.isNaN(n)))];
      
      // Also keep a set of active module names (robust fallback if FormTypeID mapping changes).
      const activeModuleNameSet = new Set(
        (activeModules || [])
          .map(m =>
            m?.ModuleName ??
            m?.moduleName ??
            m?.FormTitle ??
            m?.FormName ??
            m?.Title ??
            m?.Name
          )
          .filter(Boolean)
          .map(normalizeModuleName)
      );

      // Normalize underscores so SQL table-style names still match display titles.
      const normalizeModuleNameLoose = (value) =>
        normalizeModuleName(String(value || '').replace(/[_-]+/g, ' '));
      const activeModuleNameSetLoose = new Set(
        [...activeModuleNameSet].map(normalizeModuleNameLoose).filter(Boolean)
      );
      
      // Start with the role-based allowed items and filter by active modules
      const filteredAllowedItems = new Set();
      
      // Define basic pages that should always be available (not module-dependent)
      const basicPages = [
        'home', 'todo-list', 'calendar', 'dashboard',
        'compliance-map',
        'compliance-records', 'library-of-records',
        'settings'
      ];
      
      // Role-based R9 and invoicing pages, still gated by the R9 company module below.
      const r9Pages = pagesForModules('r9', 'awrApprovers', 'invoicing');
      const complianceOrdersMenuIds = [
        'compliance-orders',
        'compliance-orders-report',
        'compliance-order-management',
        'compliance-order-verifications',
        'compliance-order-action-codes',
        'compliance-order-work-types'
      ];
      const enginesMenuIds = [
        'add-engine',
        'add-engine-use',
        'engine-running-hours',
        'fields',
        'wells'
      ];
      const constructionPermittingItems = ['project-permitting', 'project-files'];
      const constructionAdminItems = ['manage-permit-agencies', 'manage-project-permit-types', 'manage-project-elements'];
      const verificationPages = ['audits-report', 'audits', 'audit-scopes', 'pre-audit-questionnaires', 'standards-and-regulations'];
      const moduleGatedMenuIds = new Set([
        ...Object.values(moduleToMenuItemMapping),
        ...r9Pages,
        'r9-quarterly-report',
        ...complianceOrdersMenuIds,
        ...enginesMenuIds,
        ...constructionPermittingItems,
        ...constructionAdminItems,
        'conoco-tasks',
        'conoco-task-groups',
        'conoco-tasks-section',
        ...ALL_PWS_MODULE_MENU_IDS,
        ...verificationPages
      ]);
      
      // Add basic pages that are in the original allowedItems
      basicPages.forEach(itemId => {
        if (allowedItems.has(itemId)) {
          filteredAllowedItems.add(itemId);
        }
      });
      
      // Company admin gets R9 when the module is active for an assigned company.
      const hasR9Role = userRoles.some(role => ['companyadmin', 'r9admin', 'r9manager', 'r9user'].includes(role));
      const hasR9Module = Array.isArray(activeModuleCodes) && activeModuleCodes.some(m => {
        const code = (m.code ?? '').toString().toUpperCase().trim();
        return code === 'R9' || code === 'R-9' || code === 'R9MODULE';
      });
      const hasComplianceOrdersModule = hasComplianceOrdersInActiveModules();
      if (hasR9Role && hasR9Module) {
        r9Pages.forEach(itemId => {
          if (allowedItems.has(itemId)) {
            filteredAllowedItems.add(itemId);
          }
        });
        if (allowedItems.has('r9-quarterly-report')) {
          filteredAllowedItems.add('r9-quarterly-report');
        }
      }

      // Construction permitting: construction roles + (module code OR FormType 15 OR construction module name).
      const hasConstructionModule =
        (Array.isArray(activeModuleCodes) && activeModuleCodes.some(hasConstructionModuleCode)) ||
        hasConstructionModuleByName(activeModules);
      const showConstructionPermitting =
        hasConstructionRole && (hasConstructionModule || activeFormTypeIds.includes(15));
      if (showConstructionPermitting) {
        [...constructionPermittingItems, ...constructionAdminItems].forEach((itemId) => {
          if (allowedItems.has(itemId)) {
            filteredAllowedItems.add(itemId);
          }
        });
      }
      
      // Form-type pages only when that form is active for an assigned company.
      // Other module-gated pages are added only by the explicit gates below.
      const activeFormTypeIdSet = new Set(activeFormTypeIds);
      allowedItems.forEach(itemId => {
        const formTypeIdKey = Object.keys(moduleToMenuItemMapping).find(key =>
          moduleToMenuItemMapping[key] === itemId
        );
        const formTypeIdNum = formTypeIdKey != null ? parseInt(formTypeIdKey, 10) : NaN;

        if (!Number.isNaN(formTypeIdNum)) {
          if (activeFormTypeIdSet.has(formTypeIdNum)) {
            filteredAllowedItems.add(itemId);
          }
        } else if (!moduleGatedMenuIds.has(itemId)) {
          filteredAllowedItems.add(itemId);
        }
      });

      // Fallback for ITM forms where FormTypeID mapping may not match:
      // If the backend reports these modules as active by name, ensure the matching sidebar items are visible.
      const hasAnyActiveModuleName = (namePatterns) => {
        for (const pattern of namePatterns) {
          const normPattern = normalizeModuleName(pattern);
          const loosePattern = normalizeModuleNameLoose(pattern);
          if (!normPattern && !loosePattern) continue;
          for (const activeName of activeModuleNameSet) {
            if (activeName === normPattern || activeName.includes(normPattern)) {
              return true;
            }
          }
          for (const activeName of activeModuleNameSetLoose) {
            if (activeName === loosePattern || activeName.includes(loosePattern)) {
              return true;
            }
          }
        }
        return false;
      };

      if (
        allowedItems.has('response-equipment-inventory-inspection') &&
        hasAnyActiveModuleName(['Response Equipment Inventory Inspection', 'Response Equipment Inventory'])
      ) {
        filteredAllowedItems.add('response-equipment-inventory-inspection');
      }

      if (
        allowedItems.has('equipment-deployment-exercise-log') &&
        hasAnyActiveModuleName(['Equipment Deployment Exercise Log', 'Equipment Deployment Exercise'])
      ) {
        filteredAllowedItems.add('equipment-deployment-exercise-log');
      }

      const itmNameFallbacks = [
        ['drainage-recordation', ['Drainage Recordation', 'DrainageRecordation']],
        ['spcc-routine-inspection', [
          'SPCC Routine Inspection', 'Routine SPCC Inspection',
          'SPCC_Routine', 'SPCC_Routine_Inspections', 'SPCC Routine'
        ]],
        ['spcc-annual-inspection', [
          'SPCC Annual Inspection', 'Annual SPCC Inspection',
          'SPCC_Annual', 'SPCC_Annual_Inspections', 'SPCC Annual'
        ]],
        ['swppp-discharge-monitoring-inspection', ['SWPPP Discharge Monitoring Inspection']],
        ['swppp-quarterly-inspection', ['SWPPP Quarterly Inspection', 'Quarterly SWPPP Inspection']],
        ['qi-notification-exercise-log', ['QI Notification Exercise Log']],
        ['annual-tabletop-exercise-log', ['Annual Tabletop Exercise Log']],
        ['transfer-hose-and-pipe-testing', ['Transfer Hose and Pipe Test', 'Transfer Hose and Pipe Testing']],
        ['environmental-sampling', [
          'Environmental Sampling', 'EnvironmentalSampling',
          'Environmental Sample Collection', 'Environmental Sample'
        ]],
        ['environmental-lab-analysis', [
          'Environmental Lab Analysis', 'EnvironmentalLabAnalysis', 'Environmental Lab'
        ]],
        ['hazardous-waste-removal', ['Hazardous Waste Removal']],
        ['sump-pump-maintenance-inspection', ['Sump Pump Maintenance Inspection']],
      ];
      itmNameFallbacks.forEach(([itemId, patterns]) => {
        if (allowedItems.has(itemId) && hasAnyActiveModuleName(patterns)) {
          filteredAllowedItems.add(itemId);
        }
      });

      // Environmental Sampling is a standalone CustomerModule code, not only FormType 13.
      const hasEnvironmentalSamplingModuleCode = Array.isArray(activeModuleCodes) && activeModuleCodes.some((m) => {
        const code = normalizeActiveModuleCode(m);
        return code === 'ENVIRONMENTAL_SAMPLING' || code.includes('ENVIRONMENTAL_SAMPLING');
      });
      if (hasEnvironmentalSamplingModuleCode && allowedItems.has('environmental-sampling')) {
        filteredAllowedItems.add('environmental-sampling');
        if (allowedItems.has('environmental-lab-analysis')) {
          filteredAllowedItems.add('environmental-lab-analysis');
        }
      }

      // If R9 module isn't active for any assigned company, do not show any R9 menu items
      // (even if role mapping would otherwise include them).
      if (!hasR9Module) {
        r9Pages.forEach((itemId) => {
          filteredAllowedItems.delete(itemId);
        });
        filteredAllowedItems.delete('r9-quarterly-report');
      }

      if (hasComplianceOrdersModule) {
        complianceOrdersMenuIds.forEach((itemId) => {
          if (allowedItems.has(itemId)) {
            filteredAllowedItems.add(itemId);
          }
        });
      } else {
        complianceOrdersMenuIds.forEach((itemId) => {
          filteredAllowedItems.delete(itemId);
        });
      }

      if (hasConocoTasksInActiveModules()) {
        ['conoco-tasks', 'conoco-task-groups'].forEach((itemId) => {
          if (allowedItems.has(itemId)) {
            filteredAllowedItems.add(itemId);
          }
        });
      } else {
        filteredAllowedItems.delete('conoco-tasks');
        filteredAllowedItems.delete('conoco-task-groups');
        filteredAllowedItems.delete('conoco-tasks-section');
      }

      const hasEnginesFormType = activeFormTypeIdSet.has(16);
      const hasEnginesModuleByName = hasAnyActiveModuleName(['Engines', 'Engine Running Hours']);
      const hasEnginesModuleCode = Array.isArray(activeModuleCodes) && activeModuleCodes.some((m) => {
        const code = normalizeActiveModuleCode(m);
        return code === 'ENGINES' || code === 'ENGINE';
      });
      const showEnginesMenu = hasEnginesFormType || hasEnginesModuleByName || hasEnginesModuleCode;
      if (!showEnginesMenu) {
        enginesMenuIds.forEach((itemId) => {
          if (filteredAllowedItems.has(itemId)) filteredAllowedItems.delete(itemId);
        });
      } else {
        enginesMenuIds.forEach((itemId) => {
          if (allowedItems.has(itemId)) {
            filteredAllowedItems.add(itemId);
          }
        });
      }

      // Compliance summary report stays global-admin-only; Kenedy report for global admin + PWS manager
      filteredAllowedItems.delete('compliance-summary-report');
      if (userRoles.includes('globaladmin') || userRoles.includes('pwsmanager')) {
        filteredAllowedItems.add('pws-report');
      } else {
        filteredAllowedItems.delete('pws-report');
      }

      // Environmental Sampling is not always FormType 13, and titles vary
      // ("EnvironmentalSampling", "Environmental Sample Collection"). Also honor
      // a company module code / EnvironmentalSamplingActive flag.
      if (allowedItems.has('environmental-sampling') && hasEnvironmentalSamplingActive()) {
        filteredAllowedItems.add('environmental-sampling');
      }

      // Environmental Lab Analysis should only be visible when Environmental Sampling is active for the company.
      // Enforce that dependency here: if sampling isn't allowed, hide lab analysis even if the role mapping includes it.
      if (filteredAllowedItems.has('environmental-lab-analysis') && !filteredAllowedItems.has('environmental-sampling')) {
        filteredAllowedItems.delete('environmental-lab-analysis');
      }

      // PWS + Reclaimed pages: both belong to the PWS module (FormType 18/19 or module code PWS).
      const pwsItemIds = PWS_COMPLIANCE_MENU_IDS;
      const reclaimedItemIds = RECLAIMED_MENU_IDS;
      const hasPwsModule = userHasPwsModuleActive({ activeModules, activeModuleCodes });
      const hasPwsEntitlement = userRoles.includes('globaladmin')
        || (
          userRoles.some((role) => ['companyadmin', 'pwsmanager', 'pwsuser'].includes(role))
          && userHasCompanyAssignment(user)
        );

      if (!hasPwsModule || !hasPwsEntitlement) {
        ALL_PWS_MODULE_MENU_IDS.forEach((id) => {
          if (filteredAllowedItems.has(id)) filteredAllowedItems.delete(id);
        });
      } else {
        ALL_PWS_MODULE_MENU_IDS.forEach((id) => {
          if (allowedItems.has(id)) filteredAllowedItems.add(id);
        });
      }

      // Annual Analytical Data Summary is global admin only (not part of reclaimed module access).
      if (userRoles.includes('globaladmin')) {
        filteredAllowedItems.add('annual-analytical-data-summary');
      } else {
        filteredAllowedItems.delete('annual-analytical-data-summary');
      }

      // Construction permitting: keep if role + (module OR FormType 15). Do not require both.
      if (!showConstructionPermitting) {
        [...constructionPermittingItems, ...constructionAdminItems].forEach((id) => {
          if (filteredAllowedItems.has(id)) filteredAllowedItems.delete(id);
        });
      } else {
        [...constructionPermittingItems, ...constructionAdminItems].forEach((id) => {
          if (allowedItems.has(id)) filteredAllowedItems.add(id);
        });
      }
      
      // Add section IDs if any of their children are allowed
      const sectionToChildrenMapping = {
        'inspections': ['drainage-recordation', 'spcc-routine-inspection', 'spcc-annual-inspection', 
                       'swppp-discharge-monitoring-inspection', 'swppp-quarterly-inspection',
                       'response-equipment-inventory-inspection'],
        'testing': ['qi-notification-exercise-log', 'annual-tabletop-exercise-log', 
                   'transfer-hose-and-pipe-testing', 'environmental-sampling', 'environmental-lab-analysis',
                   'equipment-deployment-exercise-log'],
        'maintenance': ['hazardous-waste-removal', 'sump-pump-maintenance-inspection'],
        'r9-facility': ['authorized-waste-receipt', 'analytical-result-form-for-awr', 
                       'outgoing-solid-waste', 'outgoing-produced-water-waste'],
        'r9-administration': [
          ...pagesForModules('awrApprovers', 'invoicing', 'r9QuarterlyReport'),
          ...pagesForModules('r9').filter((id) => (
            id === WASTE_GENERATOR_SITES_PAGE_ID
            || id === 'billing-companies'
            || id === 'waste-transporters'
            || id === DISPOSAL_SITES_PAGE_ID
          ))
        ],
        'construction-permitting': ['project-permitting', 'project-files'],
        'engines': ['fields', 'wells', 'add-engine', 'add-engine-use', 'engine-running-hours'],
        'pws-compliance': ['weekly-flow-meter-logs', 'weekly-equipment-service-logs', 'weekly-pws-water-monitoring',
                          'monthly-pws-water-sample', 'monthly-pws-analytical-report-review', 'quarterly-dlqor',
                          'quarterly-colorimeter-calibration-verification'],
        'reports': ['dashboard', 'compliance-orders-report', 'compliance-map',
                    'compliance-summary-report', 'audits-report', 'pws-report'],
        'reclaimed-water-compliance': ['weekly-reclaimed-water-monitoring', 'monthly-reclaimed-water-sample',
                                     'monthly-reclaimed-analytical-report-review', 'monthly-lab-summary',
                                     'semi-annual-reclaimed-water-sample',
                                     'semi-annual-reclaimed-analytical-report-review', 'annual-reclaimed-water-sample',
                                     'annual-reclaimed-analytical-report-review', 'annual-analytical-data-summary'],
        'compliance-orders': ['compliance-order-management', 'compliance-order-verifications', 'compliance-order-action-codes', 'compliance-order-work-types'],
        'conoco-tasks-section': ['conoco-task-groups', 'conoco-tasks']
      };
      
      // Check if any children of each section are allowed, and if so, add the section
      Object.entries(sectionToChildrenMapping).forEach(([sectionId, children]) => {
        const hasAllowedChildren = children.some(childId => filteredAllowedItems.has(childId));
        if (hasAllowedChildren) {
          filteredAllowedItems.add(sectionId);
        }
      });

      // Gate audits (audits-report + audits section) behind the VERIFICATIONS module.
      const hasVerificationsModule = Array.isArray(activeModuleCodes) && activeModuleCodes.some(hasVerificationsModuleCode);
      if (hasVerificationsModule) {
        verificationPages.forEach((id) => {
          if (allowedItems.has(id)) filteredAllowedItems.add(id);
        });
      } else {
        verificationPages.forEach((id) => {
          if (filteredAllowedItems.has(id)) filteredAllowedItems.delete(id);
        });
      }
      
      // ITM items are already filtered above by activeFormTypeIds (module-dependent items only added when FormTypeID is active). No extra block needed.
      
      allowedItems = filteredAllowedItems;
    } else if (!userRoles.some(role => ['globaladmin'].includes(role))) {
      
      // When no active modules, keep home. Compliance Records stays if they have a compliance role.
      // Other module pages stay hidden until the company/user is set up.
      const filteredAllowedItems = new Set();
      
      const basicPages = hasComplianceRole
        ? [
            'home', 'todo-list', 'calendar', 'dashboard',
            'compliance-map',
            'compliance-records', 'library-of-records',
            'settings'
          ]
        : ['home', 'todo-list', 'settings'];
      
      // Role-based R9 and invoicing pages. They still require the R9 company module.
      const r9Pages = pagesForModules('r9', 'awrApprovers', 'invoicing');
      
      // Add basic pages that are in the original allowedItems
      basicPages.forEach(itemId => {
        if (allowedItems.has(itemId)) {
          filteredAllowedItems.add(itemId);
        }
      });
      
      // Add R9 pages if user has R9 roles, the R9 module is active for at least one assigned company, and they're in the original allowedItems
      const hasR9RoleNoFormTypes = userRoles.some(role => ['companyadmin', 'r9admin', 'r9manager', 'r9user'].includes(role));
      const hasR9ModuleNoFormTypes = Array.isArray(activeModuleCodes) && activeModuleCodes.some(m => {
        const code = (m.code ?? '').toString().toUpperCase().trim();
        return code === 'R9' || code === 'R-9' || code === 'R9MODULE';
      });
      if (hasR9RoleNoFormTypes && hasR9ModuleNoFormTypes) {
        r9Pages.forEach(itemId => {
          if (allowedItems.has(itemId)) {
            filteredAllowedItems.add(itemId);
          }
        });
      }

      if (
        hasR9ModuleNoFormTypes &&
        userRoles.some(role => ['globaladmin', 'companyadmin', 'r9admin', 'r9manager'].includes(role)) &&
        allowedItems.has('r9-quarterly-report')
      ) {
        filteredAllowedItems.add('r9-quarterly-report');
      }

      const hasComplianceOrdersModuleNoFormTypes = hasComplianceOrdersInActiveModules();
      const hasComplianceOrdersRoleNoFormTypes = userRoles.some(role =>
        ['complianceordermanager', 'complianceorderuser', 'companyadmin'].includes(role)
      );
      if (hasComplianceOrdersRoleNoFormTypes && hasComplianceOrdersModuleNoFormTypes) {
        ['compliance-orders', 'compliance-orders-report', 'compliance-order-management', 'compliance-order-verifications', 'compliance-order-action-codes', 'compliance-order-work-types'].forEach(itemId => {
          if (allowedItems.has(itemId)) {
            filteredAllowedItems.add(itemId);
          }
        });
      }

      const hasEnginesModuleNoFormTypes = Array.isArray(activeModuleCodes) && activeModuleCodes.some((m) => {
        const code = normalizeActiveModuleCode(m);
        return code === 'ENGINES' || code === 'ENGINE';
      });
      const canSeeEnginesByRole = userRoles.some((role) =>
        ['globaladmin', 'companyadmin', 'facilitymanager', 'complianceordermanager', 'complianceorderuser'].includes(role)
      );
      if (canSeeEnginesByRole && hasEnginesModuleNoFormTypes) {
        ['add-engine', 'add-engine-use', 'engine-running-hours', 'fields', 'wells'].forEach((itemId) => {
          if (allowedItems.has(itemId)) {
            filteredAllowedItems.add(itemId);
          }
        });
      }

      // Invoicing for accountingmanager when no active modules (they don't get r9Pages)
      if (userRoles.includes('accountingmanager') && allowedItems.has(INVOICING_PAGE_ID)) {
        filteredAllowedItems.add(INVOICING_PAGE_ID);
      }

      if (hasEnvironmentalSamplingActive()) {
        ['environmental-sampling', 'environmental-lab-analysis'].forEach((itemId) => {
          if (allowedItems.has(itemId)) filteredAllowedItems.add(itemId);
        });
      }

      // ITM module active but no per-form metadata yet — keep role-granted ITM pages visible for admins/managers
      const hasItmModuleCodeOnly = Array.isArray(activeModuleCodes) && activeModuleCodes.some((m) => {
        const code = normalizeActiveModuleCode(m);
        return code === 'ITM';
      });
      if (hasItmModuleCodeOnly) {
        const itmRolePages = [
          'drainage-recordation', 'spcc-routine-inspection', 'spcc-annual-inspection',
          'swppp-discharge-monitoring-inspection', 'swppp-quarterly-inspection',
          'qi-notification-exercise-log', 'annual-tabletop-exercise-log', 'equipment-deployment-exercise-log',
          'transfer-hose-and-pipe-testing', 'environmental-sampling', 'environmental-lab-analysis',
          'hazardous-waste-removal', 'sump-pump-maintenance-inspection',
          'response-equipment-inventory-inspection',
        ];
        const canSeeItmByRole = userRoles.some((role) =>
          ['companyadmin', 'compliancemanager', 'facilitymanager', 'complianceuser', 'facilityuser'].includes(role)
        );
        if (canSeeItmByRole) {
          itmRolePages.forEach((itemId) => {
            if (allowedItems.has(itemId)) filteredAllowedItems.add(itemId);
          });
        }
      }

      const hasPwsModuleNoFormTypes = userHasPwsModuleActive({ activeModules: [], activeModuleCodes });
      const canSeePwsByRole = userRoles.includes('globaladmin')
        || (
          userRoles.some((role) => ['companyadmin', 'pwsmanager', 'pwsuser'].includes(role))
          && userHasCompanyAssignment(user)
        );
      if (canSeePwsByRole && hasPwsModuleNoFormTypes) {
        ALL_PWS_MODULE_MENU_IDS.forEach((itemId) => {
          if (allowedItems.has(itemId)) filteredAllowedItems.add(itemId);
        });
      }

      // Construction permitting when form-type list is empty but module codes/names indicate construction.
      const hasConstructionModuleNoFormTypes =
        (Array.isArray(activeModuleCodes) && activeModuleCodes.some(hasConstructionModuleCode)) ||
        hasConstructionModuleByName(activeModules);
      if (hasConstructionRole && hasConstructionModuleNoFormTypes) {
        ['project-permitting', 'project-files', 'manage-permit-agencies', 'manage-project-permit-types', 'manage-project-elements'].forEach((itemId) => {
          if (allowedItems.has(itemId)) filteredAllowedItems.add(itemId);
        });
      }

      if (hasConocoTasksInActiveModules()) {
        ['conoco-tasks', 'conoco-task-groups'].forEach((itemId) => {
          if (allowedItems.has(itemId)) filteredAllowedItems.add(itemId);
        });
      }
      
      const hasVerificationsModuleNoFormTypes = Array.isArray(activeModuleCodes) && activeModuleCodes.some(hasVerificationsModuleCode);
      if (hasVerificationsModuleNoFormTypes) {
        ['audits-report', 'audits', 'audit-scopes', 'pre-audit-questionnaires', 'standards-and-regulations'].forEach((id) => {
          if (allowedItems.has(id)) filteredAllowedItems.add(id);
        });
      } else {
        ['audits-report', 'audits', 'audit-scopes', 'pre-audit-questionnaires', 'standards-and-regulations'].forEach(id => {
          if (filteredAllowedItems.has(id)) filteredAllowedItems.delete(id);
        });
      }

      allowedItems = filteredAllowedItems;
    }

    const airMenuIds = ['air-glycol-pumps', 'air-flares', 'air-glycol-pump-log', 'air-opacity-monitoring', 'air-flare-log'];
    if (!userRoles.includes('globaladmin')) {
      const hasAirPermitting = (Array.isArray(activeModuleCodes) ? activeModuleCodes : []).some((entry) => (
        normalizeActiveModuleCode(entry) === 'AIR_PERMITTING'
      ));
      if (!hasAirPermitting) {
        airMenuIds.forEach((itemId) => allowedItems.delete(itemId));
      }
    }

    // Dashboard is limited to compliance-facing roles (plus global admin).
    if (!hasComplianceRole) {
      allowedItems.delete('dashboard');
    }

    if (!userHasTodoListModule(user)) {
      allowedItems.delete('todo-list');
    }

    if (isBugReportAdmin(user)) {
      allowedItems.add('bug-reports');
    }

    // Filter menu items based on allowed items; return new objects so we don't mutate shared menu structure
    const filterMenuItems = (items) => {
      const result = [];
      for (const item of items) {
        if (item.type === 'section') {
          if (item.id === 'administration') {
            const isGlobalAdmin = userRoles.some(role => role === 'globaladmin');
            if (!isGlobalAdmin) continue;
          }
          const filteredChildren = filterMenuItems(item.children || []);
          if (filteredChildren.length > 0) {
            result.push({ ...item, children: filteredChildren });
          }
        } else if (item.type === 'item') {
          if (allowedItems.has(item.id)) {
            result.push(item);
          }
        }
      }
      return result;
    };

    return filterMenuItems(menuItems);
}

export { filterNavigationMenu };
export default filterNavigationMenu;
